/**
 * Skeleton Loaders
 */

import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

/**
 * Case card skeleton loader.
 */
export function CaseCardSkeleton() {
  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
        <Skeleton variant="rounded" width={28} height={28} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="80%" height={20} />
          <Skeleton variant="text" width="50%" height={16} />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Skeleton variant="rounded" width={50} height={20} />
        <Skeleton variant="rounded" width={60} height={20} />
      </Box>
    </Box>
  );
}

/**
 * Timeline item skeleton loader.
 */
export function TimelineItemSkeleton() {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, maxWidth: '85%' }}>
      <Skeleton variant="circular" width={32} height={32} />
      <Box
        sx={{
          flex: 1,
          p: 1.5,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Skeleton variant="rounded" width={60} height={16} />
          <Skeleton variant="text" width={50} height={16} />
        </Box>
        <Skeleton variant="text" width="90%" height={18} />
        <Skeleton variant="text" width="70%" height={16} />
      </Box>
    </Box>
  );
}

export interface TableRowSkeletonProps {
  columns?: number;
}

/**
 * Table row skeleton loader.
 */
export function TableRowSkeleton({ columns = 6 }: TableRowSkeletonProps) {
  return (
    <Box sx={{ display: 'flex', gap: 2, p: 1.5 }}>
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          sx={{
            flex: i === 2 ? 2 : 1,
            height: 20,
          }}
        />
      ))}
    </Box>
  );
}

export interface TableSkeletonProps {
  /**
   * Number of skeleton rows to render.
   * @default 5
   */
  rows?: number;
  /**
   * Number of skeleton cells per row.
   * @default 6
   */
  columns?: number;
}

/**
 * Table skeleton loader — renders placeholder rows inside a table body.
 */
export function TableSkeleton({ rows = 5, columns = 6 }: TableSkeletonProps) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <TableRow key={rowIndex}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <TableCell key={colIndex}>
              <Skeleton variant="text" height={20} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

export interface ContentSkeletonProps {
  lines?: number;
}

/**
 * Generic content skeleton loader.
 */
export function ContentSkeleton({ lines = 3 }: ContentSkeletonProps) {
  return (
    <Box sx={{ p: 2 }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          sx={{
            width: i === lines - 1 ? '60%' : '100%',
            height: 20,
            mb: 1,
          }}
        />
      ))}
    </Box>
  );
}
